import { AlertTriangle, ChevronDown, ChevronUp, MapPin, Store, User } from "lucide-react";
import { useState } from "react";
import { JevEvent, JevScope, JevScore } from "interfaces/restaurantNews";

/**
 * Jev (TypeSafe) shadow scoring, as the moderator sees it on an article row: the score, what
 * Jev thinks the article reports, a "à revoir" flag when it hesitated, and the owner / venue /
 * city it picked among our regex candidates — the city with a one-click "Appliquer" when it
 * disagrees with the article's. Backend: crm-back services/news/jevScoring.ts.
 */

const FOCUS_RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

/** Same threshold as the backend's "Leads probables" chip (JEV_LEAD_SCORE). */
export const JEV_LEAD_SCORE = 70;

export const JEV_EVENT_LABEL: Record<JevEvent, string> = {
    new_opening: "Nouvelle ouverture",
    upcoming_opening: "Ouverture annoncée",
    reopening: "Réouverture",
    takeover: "Reprise",
    closing: "Fermeture",
    other_venue_news: "Autre actu du lieu",
    industry_news: "Actu du secteur",
    off_topic: "Hors sujet",
};

export const JEV_SCOPE_LABEL: Record<JevScope, string> = {
    one_venue: "Un seul lieu",
    one_chain_unit: "Unité de franchise",
    several_venues: "Liste de lieux",
    group_expansion: "Plan d'expansion",
    no_venue: "Aucun lieu précis",
};

const TIMING_LABEL: Record<string, string> = {
    already_open: "Déjà ouvert",
    announced: "Ouverture à venir",
    not_an_opening: "Pas une ouverture",
};

const pct = (p: number | null | undefined) => (p === null || p === undefined ? "" : `${Math.round(p * 100)} %`);

function scoreTone(score: number): string {
    if (score >= JEV_LEAD_SCORE) return "text-success bg-success-subtle";
    if (score >= 40) return "text-warning bg-warning-subtle";
    return "text-ink-muted bg-surface-sunken";
}

/** "Jev 82" — the row's headline number. Distinct from the Groq ScoreBadge it sits next to. */
export function JevScoreBadge({ jev }: { jev: JevScore | null | undefined }) {
    if (!jev) return null;
    if (jev.error || jev.score === undefined) {
        return (
            <span title={jev.error ?? "Scoring Jev indisponible"}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-ink-faint bg-surface-sunken">
                Jev —
            </span>
        );
    }
    return (
        <span
            title="Score Jev : probabilité que l'article annonce l'ouverture d'un seul lieu de restauration, en France"
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${scoreTone(jev.score)}`}
        >
            <span className="font-medium opacity-70">Jev</span>
            <span className="font-mono tabular-nums">{jev.score}</span>
        </span>
    );
}

/** What Jev reads the article as — plus the scope when it is not a single venue, since a
 *  roundup of ten openings is the commonest false lead. */
export function JevEventChip({ jev }: { jev: JevScore | null | undefined }) {
    if (!jev?.event) return null;
    const multi = jev.scope && jev.scope !== "one_venue" && jev.scope !== "one_chain_unit";
    return (
        <span className="inline-flex items-center gap-1 text-[10px] text-ink-muted bg-surface-sunken px-1.5 py-0.5 rounded font-medium">
            {JEV_EVENT_LABEL[jev.event]}
            {multi && jev.scope && <span className="text-ink-faint">· {JEV_SCOPE_LABEL[jev.scope]}</span>}
        </span>
    );
}

export function JevReviewBadge({ jev }: { jev: JevScore | null | undefined }) {
    if (!jev?.needsReview) return null;
    return (
        <span
            title="Jev hésite sur la nature de l'article ou le nombre de lieux : à vérifier à la main"
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-warning bg-warning-subtle px-1.5 py-0.5 rounded"
        >
            <AlertTriangle className="w-3 h-3" />
            À revoir
        </span>
    );
}

function Bar({ label, value }: { label: string; value: number }) {
    return (
        <div className="grid grid-cols-[minmax(0,1fr)_96px_36px] items-center gap-2">
            <span className="text-[11px] text-ink-muted truncate">{label}</span>
            <span className="h-1.5 rounded-full bg-surface-sunken overflow-hidden">
                <span className="block h-full rounded-full bg-ink" style={{ width: `${Math.round(value * 100)}%` }} />
            </span>
            <span className="text-[11px] font-mono tabular-nums text-ink text-right">{pct(value)}</span>
        </div>
    );
}

/**
 * The extraction line under the title (owner · venue · city), and a "Détail Jev" fold with
 * the event probabilities and the other answers. Renders nothing for an unscored article.
 */
export function JevExtraction({
    jev,
    articleCity,
    onApplyCity,
}: {
    jev: JevScore | null | undefined;
    articleCity: string | null;
    onApplyCity: (city: string) => Promise<void>;
}) {
    const [open, setOpen] = useState(false);
    const [applying, setApplying] = useState(false);
    if (!jev || jev.error || jev.score === undefined) return null;

    const cityDiffers = !!jev.city && jev.city.localeCompare(articleCity ?? "", "fr", { sensitivity: "base" }) !== 0;
    const otherOwners = (jev.people ?? []).filter((p) => p.role === "owner_founder" && p.name !== jev.ownerName);
    const events = Object.entries(jev.eventProbabilities ?? {})
        .filter(([, p]) => (p ?? 0) >= 0.03)
        .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0)) as [JevEvent, number][];

    const applyCity = async () => {
        if (!jev.city) return;
        setApplying(true);
        try {
            await onApplyCity(jev.city);
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="mt-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                {jev.ownerName && (
                    <span className="inline-flex items-center gap-1 text-ink" title={`Proprio selon Jev (${pct(jev.ownerProb)})`}>
                        <User className="w-3 h-3 text-ink-faint" />
                        {jev.ownerName}
                        {otherOwners.length > 0 && <span className="text-ink-faint">+ {otherOwners.map((p) => p.name).join(", ")}</span>}
                    </span>
                )}
                {jev.restaurantName && (
                    <span className="inline-flex items-center gap-1 text-ink" title={`Nom du lieu selon Jev (${pct(jev.restaurantProb)})`}>
                        <Store className="w-3 h-3 text-ink-faint" />
                        {jev.restaurantName}
                    </span>
                )}
                {jev.city && (
                    <span className="inline-flex items-center gap-1 text-ink" title={`Ville selon Jev (${pct(jev.cityProb)})`}>
                        <MapPin className="w-3 h-3 text-ink-faint" />
                        {jev.city}
                        {cityDiffers && (
                            <button
                                onClick={applyCity}
                                disabled={applying}
                                title={articleCity ? `Remplacer « ${articleCity} » par « ${jev.city} »` : `Renseigner « ${jev.city} »`}
                                className={`ml-1 text-[10px] font-medium text-accent hover:underline disabled:opacity-40 rounded ${FOCUS_RING}`}
                            >
                                {applying ? "…" : articleCity ? `Appliquer (au lieu de ${articleCity})` : "Appliquer"}
                            </button>
                        )}
                    </span>
                )}
                <button
                    onClick={() => setOpen((o) => !o)}
                    aria-expanded={open}
                    className={`inline-flex items-center gap-0.5 text-ink-faint hover:text-ink transition-colors rounded ${FOCUS_RING}`}
                >
                    Détail Jev
                    {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
            </div>

            {open && (
                <div className="mt-2 grid gap-4 rounded-lg border border-border bg-surface-sunken/40 p-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-faint">Que rapporte l'article ?</p>
                        {events.map(([k, p]) => <Bar key={k} label={JEV_EVENT_LABEL[k]} value={p} />)}
                        <p className="text-[11px] text-ink-faint">Confiance {pct(jev.eventConfidence)}</p>
                    </div>
                    <div className="space-y-1.5 text-[11px]">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-faint">Autres réponses</p>
                        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
                            <dt className="text-ink-muted">Lieux</dt>
                            <dd className="text-ink">{jev.scope ? JEV_SCOPE_LABEL[jev.scope] : "—"} <span className="text-ink-faint">{pct(jev.scopeConfidence)}</span></dd>
                            <dt className="text-ink-muted">Ouverture</dt>
                            <dd className="text-ink">{jev.timing ? TIMING_LABEL[jev.timing] : "—"}</dd>
                            <dt className="text-ink-muted">En France</dt>
                            <dd className="text-ink font-mono tabular-nums">{pct(jev.inFrance)}</dd>
                            <dt className="text-ink-muted">Franchise</dt>
                            <dd className="text-ink font-mono tabular-nums">{pct(jev.franchise)}</dd>
                            <dt className="text-ink-muted">Identification</dt>
                            <dd className="text-ink font-mono tabular-nums">{jev.identification?.toFixed(1) ?? "—"} / 3</dd>
                            {(jev.people ?? []).length > 0 && (
                                <>
                                    <dt className="text-ink-muted">Personnes</dt>
                                    <dd className="text-ink">
                                        {(jev.people ?? []).map((p) => (
                                            <span key={p.name} className="block">
                                                {p.name} <span className="text-ink-faint">· {ROLE_LABEL[p.role]} {pct(p.role === "owner_founder" ? p.ownerProb : p.confidence)}</span>
                                            </span>
                                        ))}
                                    </dd>
                                </>
                            )}
                        </dl>
                    </div>
                </div>
            )}
        </div>
    );
}

const ROLE_LABEL: Record<string, string> = {
    owner_founder: "proprio / fondateur",
    chef_employee: "chef / salarié",
    other_person: "autre personne",
    not_a_person: "pas une personne",
};
